import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * The element's content size, kept current as it is resized. Null until first measured.
 *
 * The size is the layout size, before any CSS transform - so on the TV stage, which is scaled to
 * fit the screen, it is in the stage's own 1920 x 1080 pixels.
 */
export function useElementSize<T extends HTMLElement>(): [
  RefObject<T>,
  { width: number; height: number } | null,
] {
  const ref = useRef<T>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, size];
}
